import { useState, useRef, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Boxes, Loader2, MessageSquare, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [session, setSession] = useState(null);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const otpRef = useRef();

  useEffect(() => { if (step === 2) setTimeout(() => otpRef.current?.focus(), 100); }, [step]);

  if (user) return <Navigate to="/" replace />;

  const doLogin = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.success) {
      setSession(res.session);
      setStep(2);
      // Only show dev OTP toast in non-production (backend opts in via OTP_DEV_MODE env).
      if (res.session.dev_otp) toast.info(`Dev OTP: ${res.session.dev_otp}`, { duration: 10000 });
    } else setError(res.error);
  };

  const doVerify = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await verifyOtp(session.session_token, code.trim());
    setLoading(false);
    if (res.success) {
      toast.success("Welcome to eaassy");
      if (res.must_change_password) navigate("/change-password?forced=1");
      else navigate("/");
    } else setError(res.error);
  };

  return (
    <div className="min-h-screen flex bg-white">
      <div className="w-full md:w-1/2 flex flex-col justify-center px-8 md:px-24" data-testid="login-pane">
        <div className="max-w-sm w-full">
          <div className="flex items-center gap-2 mb-12">
            <Boxes className="w-6 h-6 text-slate-900" strokeWidth={2} />
            <span className="font-bold tracking-tight text-slate-900" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>eaassy</span>
          </div>

          {step === 1 && (
            <>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">Sign in</div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight leading-none text-slate-900 mb-3" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
                Welcome back.
              </h1>
              <p className="text-slate-500 mb-10">Multi-branch retail, billing and intelligence — in one.</p>
              <form onSubmit={doLogin} className="space-y-5" data-testid="login-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-700">Email</Label>
                  <Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email"
                    className="mt-2 rounded-none h-11 border-slate-300" placeholder="you@company.com" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-700">Password</Label>
                  <Input required type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password"
                    className="mt-2 rounded-none h-11 border-slate-300" placeholder="••••••••" />
                </div>
                {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
                <Button type="submit" disabled={loading} data-testid="login-submit"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-none">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Continue"}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <button onClick={() => { setStep(1); setCode(""); setError(""); }} className="text-xs text-slate-500 mb-6 flex items-center gap-1 hover:text-slate-900" data-testid="otp-back">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3 flex items-center gap-2">
                <MessageSquare className="w-3 h-3" /> 2-step verification
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-3" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
                Check WhatsApp.
              </h1>
              <p className="text-slate-500 mb-2">
                We sent a 6-digit code to <span className="font-mono">{session?.masked_phone}</span>
              </p>
              {session?.sent_via === "dev" && session?.dev_otp && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2 mb-6" data-testid="dev-mode-banner">
                  DEV MODE · Your OTP is <span className="font-mono font-bold">{session.dev_otp}</span>
                </p>
              )}
              {session?.sent_via === "dev" && !session?.dev_otp && (
                <p className="text-xs text-slate-600 bg-slate-50 border border-slate-200 p-2 mb-6" data-testid="otp-sent-info">
                  OTP delivery channel is unavailable. Please contact your administrator.
                </p>
              )}
              <form onSubmit={doVerify} className="space-y-5 mt-6" data-testid="otp-form">
                <div>
                  <Label className="text-xs uppercase tracking-[0.15em] text-slate-700">OTP Code</Label>
                  <Input ref={otpRef} required maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} data-testid="otp-code-input"
                    className="mt-2 rounded-none h-14 border-slate-300 font-mono tracking-[0.4em] text-xl text-center" placeholder="••••••" />
                </div>
                {error && <div className="text-sm text-red-600" data-testid="otp-error">{error}</div>}
                <Button type="submit" disabled={loading} data-testid="otp-verify"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 rounded-none">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Sign in"}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
      <div className="hidden md:block w-1/2 bg-slate-900 relative overflow-hidden">
        <img src="https://images.unsplash.com/photo-1711873316332-acb6930211e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjByZXRhaWwlMjBzdG9yZSUyMHdoaXRlJTIwbWluaW1hbHxlbnwwfHx8fDE3Nzc0NDYwMTl8MA&ixlib=rb-4.1.0&q=85"
          alt="" className="absolute inset-0 w-full h-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/60 via-slate-900/40 to-slate-950/80" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <div className="text-xs uppercase tracking-[0.3em] text-white/60 mb-3">Multi-Tenant Retail OS</div>
          <h2 className="text-3xl font-bold tracking-tight leading-tight" style={{ fontFamily: "Cabinet Grotesk, IBM Plex Sans, sans-serif" }}>
            One outlet, many branches.<br/>Every bill, every rupee.
          </h2>
        </div>
      </div>
    </div>
  );
}
